import React, { useState, useEffect, useRef } from 'react';
import { User, Search, Check, Loader2, X, Link2, Unlink } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { customFetch, getAuthHeaders } from '../../services/apiService';

export interface AuthorUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface AuthorAsyncAutocompleteProps {
  value: string;
  authorId: string | null;
  onChange: (authorName: string, authorId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const AuthorAsyncAutocomplete: React.FC<AuthorAsyncAutocompleteProps> = ({
  value,
  authorId,
  onChange,
  placeholder = 'Mặc định: Nhà Giáo Số hoặc tìm kiếm tác giả...',
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<AuthorUser[]>([]);
  const [selectedUserCache, setSelectedUserCache] = useState<AuthorUser | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(inputValue, 500);

  // Sync external value to local input
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch search results when debounced query changes
  useEffect(() => {
    let isCancelled = false;

    // Only search if dropdown is open or input has focus
    if (!isOpen) return;

    const fetchAuthors = async () => {
      setIsLoading(true);
      try {
        const queryParam = encodeURIComponent(debouncedQuery.trim());
        const res = await customFetch(`/api/users/search-authors?q=${queryParam}&limit=10`, {
          headers: getAuthHeaders(),
        });
        if (!isCancelled && res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.users)) {
            setUsers(data.users);
            // If current authorId matches one of the fetched users, cache their info
            if (authorId) {
              const matched = data.users.find((u: AuthorUser) => u.id === authorId);
              if (matched) setSelectedUserCache(matched);
            }
          } else {
            setUsers([]);
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Lỗi tìm kiếm tác giả:', err);
          setUsers([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAuthors();

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery, isOpen, authorId]);

  // Handle user typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    setIsOpen(true);

    // If user changes text away from the linked author's name, reset authorId to null
    if (authorId && selectedUserCache && newVal.trim() !== selectedUserCache.name.trim()) {
      onChange(newVal, null);
      setSelectedUserCache(null);
    } else {
      onChange(newVal, authorId);
    }
  };

  // Handle selecting user from dropdown
  const handleSelectUser = (user: AuthorUser) => {
    setInputValue(user.name);
    setSelectedUserCache(user);
    onChange(user.name, user.id);
    setIsOpen(false);
  };

  // Handle unlinking author without clearing custom name
  const handleUnlink = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(inputValue, null);
    setSelectedUserCache(null);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input container */}
      <div className="relative flex items-center">
        <input
          type="text"
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full pl-8 pr-16 py-2 text-xs font-semibold border rounded-xl transition-all focus:outline-none focus:ring-2 ${
            authorId
              ? 'border-purple-300 bg-purple-50/20 text-purple-950 focus:ring-purple-500'
              : 'border-slate-200 text-slate-800 focus:ring-purple-500'
          }`}
        />

        {/* Left Icon: User or small linked status */}
        <div className="absolute left-2.5 flex items-center pointer-events-none text-slate-400">
          {authorId ? (
            <Link2 className="w-3.5 h-3.5 text-purple-600" />
          ) : (
            <Search className="w-3.5 h-3.5 text-slate-400" />
          )}
        </div>

        {/* Right Status Actions */}
        <div className="absolute right-2 flex items-center space-x-1">
          {isLoading && (
            <Loader2 className="w-3.5 h-3.5 text-purple-600 animate-spin" />
          )}

          {authorId && (
            <button
              type="button"
              title="Hủy liên kết tài khoản (chuyển sang tác giả ngoài)"
              onClick={handleUnlink}
              className="px-1.5 py-0.5 text-[10px] font-bold text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-md transition-colors flex items-center space-x-0.5 cursor-pointer"
            >
              <Unlink className="w-3 h-3" />
              <span>Gỡ ID</span>
            </button>
          )}
        </div>
      </div>

      {/* Linked Account Indicator / Tag */}
      {authorId && (
        <div className="mt-1 flex items-center justify-between px-2 py-1 bg-purple-50/80 border border-purple-100 rounded-lg text-[11px] text-purple-800">
          <div className="flex items-center space-x-1.5 truncate">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-600 shrink-0" />
            <span className="font-semibold truncate">
              {selectedUserCache ? `${selectedUserCache.name} (${selectedUserCache.email})` : `ID User: ${authorId}`}
            </span>
          </div>
          <span className="text-[10px] font-bold text-purple-600 bg-white px-1.5 py-0.5 rounded shadow-2xs shrink-0 ml-1">
            Đã gắn User
          </span>
        </div>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-fade-in max-h-60 overflow-y-auto">
          {/* Header of dropdown */}
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-[11px] text-slate-500 font-bold">
            <span>Gợi ý tài khoản hệ thống</span>
            {isLoading ? (
              <span className="text-purple-600 font-normal flex items-center space-x-1">
                <Loader2 className="w-3 h-3 animate-spin inline" />
                <span>Đang tìm...</span>
              </span>
            ) : (
              <span>{users.length} kết quả</span>
            )}
          </div>

          {/* User list */}
          {users.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {users.map((user) => {
                const isSelected = authorId === user.id;
                return (
                  <div
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className={`px-3 py-2.5 flex items-center space-x-2.5 cursor-pointer transition-colors ${
                      isSelected ? 'bg-purple-50/80' : 'hover:bg-slate-50'
                    }`}
                  >
                    {/* User Avatar */}
                    <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-slate-200 bg-slate-100 flex items-center justify-center">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            (e.currentTarget as HTMLElement).style.display = 'none';
                            const parent = (e.currentTarget as HTMLElement).parentElement;
                            if (parent) parent.innerText = user.name?.charAt(0).toUpperCase() || 'U';
                          }}
                        />
                      ) : (
                        <span className="text-xs font-bold text-slate-600">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>

                    {/* User Name & Email */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-xs font-bold text-slate-800 truncate">
                          {user.name}
                        </span>
                        {isSelected && (
                          <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-1 rounded">
                            Đang chọn
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 truncate">
                        {user.email}
                      </p>
                    </div>

                    {/* Checkmark icon */}
                    {isSelected && (
                      <Check className="w-4 h-4 text-purple-600 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : !isLoading ? (
            <div className="p-3 text-center text-xs text-slate-400">
              <p>Không tìm thấy tài khoản người dùng phù hợp.</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Bạn vẫn có thể lưu tên tác giả tự gõ ở trên cho tài liệu nguồn ngoài.
              </p>
            </div>
          ) : null}

          {/* Option to keep custom text */}
          {inputValue.trim() && (
            <div
              onClick={() => {
                onChange(inputValue, null);
                setSelectedUserCache(null);
                setIsOpen(false);
              }}
              className="p-2 bg-slate-50 hover:bg-slate-100 border-t border-slate-100 text-[11px] text-slate-600 cursor-pointer flex items-center justify-between"
            >
              <span className="truncate">
                Dùng tên tùy ý: <strong>&ldquo;{inputValue}&rdquo;</strong> (nguồn ngoài)
              </span>
              <span className="text-[10px] font-semibold text-slate-400 shrink-0 ml-2">Không gắn ID</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
